/**
 * Header.jsx
 * Wavy navbar links with smooth scroll and static Home support
 */

import React, { useState, useEffect, useRef } from "react";
import { MotionConfig, motion, useReducedMotion } from "framer-motion";
import "../../styles/Header.css";
import logo from "../../assets/icity_logo.png";

const linkItems = [
  { id: "home", label: "Home", type: "static" },
  { id: "about", label: "About Us", type: "scroll" },
  { id: "services", label: "Our Services", type: "scroll" },
  { id: "portfolio", label: "Gallery", type: "scroll" },
  { id: "contact", label: "Contact Us", type: "scroll" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [scrolled, setScrolled] = useState(false);

  const indicatorRef = useRef(null);
  const navRef = useRef(null);
  const headerRef = useRef(null); // ✅ NEW: wrapper ref
  const prefersReduced = useReducedMotion();

  // ✅ Close menu if clicking outside the header
  useEffect(() => {
    function handleClickOutside(e) {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Move nav indicator under active link
  const moveIndicator = () => {
    const nav = navRef.current;
    const indicator = indicatorRef.current;
    if (!nav || !indicator) return;
    const activeLink = nav.querySelector(`a.active`);
    if (!activeLink) return;
    const { offsetLeft, offsetWidth } = activeLink;
    indicator.style.transform = `translateX(${offsetLeft}px)`;
    indicator.style.width = `${offsetWidth}px`;
    indicator.style.opacity = 1;
  };

  // Handle scroll to update active link
  useEffect(() => {
    const sections = linkItems.filter(l => l.type === "scroll").map(l => l.id);
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        let current = "home";
        sections.forEach(id => {
          const section = document.getElementById(id);
          if (section) {
            const sectionTop = section.offsetTop - 90;
            if (window.scrollY >= sectionTop) current = id;
          }
        });
        setActive(current);
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", moveIndicator);
    onScroll(); // initialize on load

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", moveIndicator);
    };
  }, []);

  useEffect(() => {
    moveIndicator();
  }, [active, isOpen]);

  // Handle nav click: scroll or static
  const handleNavClick = (id, type) => (e) => {
    setActive(id);
    setIsOpen(false);

    if (type === "scroll") {
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else if (type === "static") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const toggleByKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((v) => !v);
    }
  };

  // Wavy animation for text
  const waveText = prefersReduced
    ? {}
    : {
        whileHover: { y: [0, -2, 0, 2, 0],
          transition: { duration: 0.6, times: [0, 0.25, 0.5, 0.75, 1], ease: "easeOut" } },
        whileFocus: { y: [0, -2, 0, 2, 0],
          transition: { duration: 0.6, times: [0, 0.25, 0.5, 0.75, 1], ease: "easeOut" } },
      };

  const waveUnderline = prefersReduced
    ? {}
    : {
        initial: { scaleX: 0, opacity: 0.8 },
        whileHover: { scaleX: 1.08, opacity: 1,
          transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] } },
        whileFocus: { scaleX: 1.08, opacity: 1,
          transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] } },
      };

  return (
    <MotionConfig reducedMotion="user">
      <header ref={headerRef} className={`navbar ${scrolled ? "is-scrolled" : ""}`}>
        {/* ---- LEFT: Logo ---- */}
        <a
          href="#home"
          className="navbar-logo"
          aria-label="Brand home"
          onClick={handleNavClick("home", "static")}
        >
          <img src={logo} alt="Brand Logo" />
        </a>

        {/* ---- CENTER: Navigation Links ---- */}
        <nav
          ref={navRef}
          className={`nav-links ${isOpen ? "open" : ""}`}
          aria-label="Primary"
          id="primary-navigation"
        >
          <span
            className="nav-indicator"
            ref={indicatorRef}
            aria-hidden="true"
          />
          {linkItems.map(({ id, label, type }) => (
            <motion.a
              key={id}
              href={`#${id}`}
              className={active === id ? "active" : ""}
              onClick={handleNavClick(id, type)}
              {...waveText}
            >
              <span className="link-label">{label}</span>
              <motion.span
                className="wave-underline"
                {...waveUnderline}
                aria-hidden="true"
              />
            </motion.a>
          ))}
        </nav>

        {/* ---- RIGHT: CTA Buttons ---- */}
        <div className="cta-wrap">
          <a href="#contact" className="cta-pill">Book Now</a>
          <a href="#contact" className="cta-circle" aria-label="Go">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M8 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
                  {/* ---- HAMBURGER ---- */}
        <button
          className={`hamburger ${isOpen ? "is-active" : ""}`}
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
          aria-controls="primary-navigation"
          onClick={() => setIsOpen(o => !o)}
          onKeyDown={toggleByKey}
        >
          {isOpen ? (
            /* Close icon */
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            /* Hamburger icon */
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
        </div>


      </header>
    </MotionConfig>
  );
};

export default Header;
